import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react"
import { useDispatch } from "react-redux"
import { get, size, has } from "lodash"
import ClassNames from "classnames"

import {
  ForeignBasePanel,
  ForeignIndexTable,
  ForeignSymbolList,
} from "components/organisms/panel/foreign"
import { Panel } from "components/organisms/panel"
import MiddleChart from "components/molecules/chart/middleChart"
import { InfoTable } from "components/molecules/table"
import { infoTableColumns } from "config/table_columns"
import { RankingSummaryPanel } from "components/organisms/panel/rankingSummary"
import { APIResponse } from "redux/apis/types"
import { calledForeignData } from "components/actions/foreign"
import { CountryListType } from "components/pages/main/foreign"
import { changeSymbol } from "redux/ducks/Chart"
import { BaseSelect } from "components/atoms/input/select"
import IndicatorChartComponent from "components/molecules/chart/indicatorChart"
import { NewsList } from "components/organisms/panel/newsList"
import { getRoute } from "utils/util"
import { Tab } from "components/atoms/btn/tab"
import { checkIsSp } from "utils/util"
import { MainContainer } from "components/molecules/utils/mainContainer"
import APIs from "../../../redux/apis"
import { useLodingTrigger, LoadingTriger } from "components/actions/loading"
import { SECURITY_TYPE } from "config/const"
import { DataDescription } from "../../organisms/panel/dataDescription"
import { ForeignEquityDisclaimerText } from "config/disclaimerText"

type Option = {
  id: number | string
  name: string
  priceIncrease: number
  priceReduction: number
  volume: number
}

type ForeignTemplateType = {
  countryInfo: CountryListType
  rankingData: RankingDataType
  changeRanking: Function
  rankingList?: Array<Option>
  callApi: Function
  listData: calledForeignData
  symbolData: APIResponse.Res
  setSymbolData: Dispatch<SetStateAction<APIResponse.Res>>
  symbolList: APIResponse.Res
  currentSymbolPage: number
  symbolListChangePage: Function
  selectList: number
  setSelectList: Function
  canvasId: string
  countryCode: string
  changeCurrentTab?: Function
  setChangeTabFlag: Function
}

type RankingDataType = {
  exchangeTypeNum: number
  priceIncreaseList: Array<object>
  priceReductionList: Array<object>
  volumeList: Array<object>
  priceIncreaseTime: string
  priceReductionTime: string
  volumeTime: string
}

export const ForeignTitle = (icon: string, name: string) => (
  <>
    <i className={ClassNames("icon-solid icon-m", `icon-${icon}`)} />
    <span>{name}</span>
  </>
)

export const ForeignTemplate: React.FC<ForeignTemplateType> = props => {
  const labels = [{ id: 0, name: "サマリー" }, { id: 1, name: "銘柄リスト" }]
  const [templateType, changeTemplate] = useState(labels[0].id)
  const [indicatorData, changeIndicatorData] = useState([])
  const [indicatorDay, changeIndicatorDay] = useState(0)
  const [indicatorPage, changeIndicatorPage] = useState(0)
  //サマリと銘柄リストで、タブ切り替え時に呼ぶAPIを切り分けたいのでどちらでも呼ぶニュースはステート切り分け
  const [newsLoadFlag, setNewsLoadFlag] = useState(false)
  const indicatorApiParam = [
    `Country.${props.countryCode}.Today`,
    `Country.${props.countryCode}.PrevDays`,
    `Country.${props.countryCode}.NextDays`,
  ]
  const indicatorItem = {
    data: indicatorData,
    changeData: changeIndicatorData,
    selectDay: indicatorDay,
    changeDay: changeIndicatorDay,
    currentPage: indicatorPage,
    changePage: changeIndicatorPage,
  }

  const prevTemplateType = useRef(null)
  const dispatch = useDispatch()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const { loadingTrigger } = useLodingTrigger()

  useEffect(() => {
    if (templateType !== prevTemplateType.current) {
      const firstSymbol = !templateType
        ? get(props.listData, "index.ResultList", [])
        : get(props.symbolList, "ResultList", [])
      if (!!size(firstSymbol)) {
        prevTemplateType.current = templateType
        props.setSymbolData(firstSymbol[0])
        dispatch(changeSymbol(props.canvasId, firstSymbol[0]))
      }
    }
  }, [templateType, props.symbolData, props.symbolList])

  //初期ロード用
  useEffect(() => {
    if (!!props.listData && !prevTemplateType.current && !props.symbolData) {
      const indexData = get(props.listData, "index.ResultList", [])
      !isPhone && props.setSymbolData(indexData[0])
      !isPhone && dispatch(changeSymbol(props.canvasId, indexData[0]))
      prevTemplateType.current = templateType
    }
  }, [props.listData])

  useEffect(() => {
    ;(async () => {
      const indicatorList:
        | APIResponse.Res
        | {} = await APIs.SymbolDetail.IndicatorList({
        Name: indicatorApiParam[indicatorDay],
        // _take: PAGE_NUM.PAGE_10,
        // _skip: PAGE_NUM.PAGE_10 * indicatorPage,
      })
      changeIndicatorData(get(indicatorList, "ResultList"))
    })()
  }, [indicatorDay])

  return (
    <MainContainer className="foreign-each-template">
      <div
        className={ClassNames("symmetry-layout foreign-each", {
          summary: templateType === labels[0].id,
        })}
      >
        <div className="contents-block foreign-list-block">
          <ForeignBasePanel
            countryInfo={props.countryInfo}
            listData={props.listData}
            filter={
              <>
                <Tab
                  className="foot-border-tabs nav-tabs foreign-summary-tabs"
                  list={labels}
                  active={templateType}
                  onChange={(id: number) => {
                    changeTemplate(id)
                    props.changeCurrentTab(id)
                    props.setChangeTabFlag(true)
                    setNewsLoadFlag(true)
                    loadingTrigger.setLoading(true)
                  }}
                />
                {!!templateType &&
                props.countryInfo.selectList.length !== 1 &&
                !isPhone ? (
                  <BaseSelect
                    option={props.countryInfo.selectList}
                    value={props.selectList}
                    onChange={(id: number) => props.setSelectList(id)}
                  />
                ) : null}
              </>
            }
            templateType={templateType}
            symbolData={props.symbolData}
            setSymbolData={props.setSymbolData}
            symbolList={props.symbolList}
            currentSymbolPage={props.currentSymbolPage}
            symbolListChangePage={props.symbolListChangePage}
            indicatorItem={indicatorItem}
            selectList={props.selectList}
            setSelectList={props.setSelectList}
          />
        </div>
        <div className="contents-block side-symbol-block">
          <div className="contents-nest-block">
            {!isPhone ? (
              <Panel
                className="panel-main-index"
                title=""
                nonePanelHeader={true}
              >
                {!props.symbolData || has(props.symbolData, "Symbol") ? (
                  <>
                    <MiddleChart id={props.canvasId} />
                    {(get(props.symbolData, "Symbol.SecurityType") ===
                      SECURITY_TYPE.Index.id ||
                      templateType === labels[1].id) && (
                      <InfoTable
                        list={infoTableColumns.common_list}
                        data={get(props.symbolData, "Symbol")}
                        className="table-striped table-index-detail"
                        wrapperCls="index-detail-table-row"
                      />
                    )}
                  </>
                ) : (
                  <IndicatorChartComponent canvasId={props.canvasId} />
                )}
              </Panel>
            ) : null}
            <RankingSummaryPanel
              title="ランキング"
              rankingData={props.rankingData}
              exchangeList={props.rankingList}
              changeRanking={props.changeRanking}
              callApi={props.callApi}
            />
            <NewsList
              route={getRoute(props.countryInfo.code)}
              effect={newsLoadFlag}
              setEffect={setNewsLoadFlag}
            />
          </div>
        </div>
      </div>
      {!isPhone && templateType === 0 && (
        <DataDescription
          className="panel-data-description"
          children={ForeignEquityDisclaimerText}
        />
      )}
    </MainContainer>
  )
}

type ForeignIsraelPropsType = {
  countryInfo: CountryListType
  listData: calledForeignData
  symbolList: APIResponse.Res
  symbolPage: number
  changeSymbolPage: (n: number) => void
}
export const ForeignIsrael: React.FC<ForeignIsraelPropsType> = props => {
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  return (
    <MainContainer className="foreign-israel-template">
      <div className="symmetry-layout">
        <div className="contents-block">
          <Panel
            title={ForeignTitle(props.countryInfo.icon, props.countryInfo.name)}
            className="panel-summary-foreign israel"
            filter={
              <div className="link-item">
                <i className="material-icons">library_books</i>
                <span
                  className="link-text"
                  onClick={() => {
                    window.open(
                      "https://www.aizawa.co.jp/documents/reports/israel.pdf"
                    )
                  }}
                >
                  イスラエル株価情報
                </span>
              </div>
            }
          >
            <ForeignIndexTable listData={props.listData} />
          </Panel>
        </div>
        <div className="contents-block side-symbol-block">
          <Panel
            title=""
            className="panel-summary-foreign"
            nonePanelHeader={true}
          >
            <ForeignSymbolList
              list={props.symbolList}
              current={props.symbolPage}
              changePage={props.changeSymbolPage}
            />
          </Panel>
        </div>
      </div>
      {!isPhone && (
        <DataDescription
          className="panel-data-description"
          children={ForeignEquityDisclaimerText}
        />
      )}
    </MainContainer>
  )
}
